import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { api } from "../lib/api";
import type { AgentWorkbenchPatch, AgentWorkSurfaceManifest, BrandStrategy } from "../../../shared/types";

export type StrategyPatchRequest = {
  target_section_id?: string | null;
  quick_action_id?: string | null;
  instruction?: string | null;
};

export function useStrategy(brandId: string | undefined) {
  return useQuery({
    queryKey: ["strategy", brandId],
    queryFn: () => api.get<{ data: BrandStrategy | null }>(`/api/brands/${brandId}/strategy`),
    enabled: !!brandId,
  });
}

export function useUpdateStrategy(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: Partial<BrandStrategy>) =>
      api.put<{ data: BrandStrategy }>(`/api/brands/${brandId}/strategy`, patch),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["strategy", brandId] });
      qc.invalidateQueries({ queryKey: ["brands", brandId] });
      qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

export function useConfirmStrategy(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => api.post<{ data: BrandStrategy }>(`/api/brands/${brandId}/strategy/confirm`, {}),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["strategy", brandId] });
      qc.invalidateQueries({ queryKey: ["brands", brandId] });
      qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}

export function useBrandStrategyWorkbench(brandId: string | undefined) {
  return useQuery({
    queryKey: ["brands", brandId, "strategy", "workbench"],
    queryFn: () =>
      api.get<{ data: AgentWorkSurfaceManifest }>(`/api/agent-workbench/strategy_artifact/${brandId}`),
    enabled: !!brandId,
  });
}

export function useProposeBrandStrategyWorkbenchPatch(brandId: string) {
  return useMutation({
    mutationFn: (body: StrategyPatchRequest) =>
      api.post<{ data: AgentWorkbenchPatch }>(
        `/api/agent-workbench/strategy_artifact/${brandId}/propose`,
        body,
      ),
  });
}

export function useApplyBrandStrategyWorkbenchPatch(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patch: AgentWorkbenchPatch) =>
      api.post<{ data: AgentWorkSurfaceManifest }>(
        `/api/agent-workbench/strategy_artifact/${brandId}/apply`,
        { patch },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["brands", brandId, "strategy", "workbench"] });
      qc.invalidateQueries({ queryKey: ["strategy", brandId] });
      qc.invalidateQueries({ queryKey: ["brands", brandId] });
      qc.invalidateQueries({ queryKey: ["brands"] });
    },
  });
}
